import { Group, Mesh, type Material } from 'three';
import type { LinearRGB } from '../../core/color';
import { createMeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { beamSolid, boxSolid } from '../fabric/solids';

export const TRADE_SCALE = { arm: 1.35, pivot: 2.65, chain: 1.9, panTop: 0.02 } as const;

/** A working balance is a reconstruction of the weighing process, not a measured surviving apparatus. */
export function buildTradeScale(
  material: Material,
  timber: LinearRGB,
  iron: LinearRGB,
  cloth: LinearRGB
) {
  const frame = { origin: [0, 0, 0] as const, yaw: 0 };
  const style = (color: LinearRGB, surface: VertexStyle['surface']): VertexStyle => ({
    color,
    surface,
    edgeId: 891,
    strokeAngle: 0,
    extent: [1, 1],
  });
  const wood = style(timber, SURFACE.wood),
    metal = style(iron, SURFACE.wood);
  const fixed = createMeshBuilder(512);
  for (const x of [-2.15, 2.15])
    for (const z of [-1.15, 1.15])
      boxSolid(fixed, wood, frame, [x - 0.09, -0.1, z - 0.09], [x + 0.09, 3.4, z + 0.09]);
  boxSolid(fixed, wood, frame, [-2.3, 3.35, -1.3], [2.3, 3.43, 1.3]);
  boxSolid(fixed, style(cloth, SURFACE.cloth), frame, [-2.3, 3.44, -1.3], [2.3, 3.48, 1.3]);
  boxSolid(fixed, wood, frame, [-1.85, 1.45, 0.6], [-0.85, 1.53, 1.1]);
  for (const x of [-1.78, -0.98])
    for (const z of [0.65, 1.05])
      boxSolid(fixed, wood, frame, [x - 0.035, 0, z - 0.035], [x + 0.035, 1.45, z + 0.035]);
  beamSolid(fixed, metal, frame, [0, 3.35, 0], [0, TRADE_SCALE.pivot, 0], 0.028);
  const support = new Mesh(fixed.finish(), material);
  support.name = 'weighing-awning';
  const moving = createMeshBuilder(64);
  boxSolid(moving, wood, frame, [-1.6, -0.05, -0.055], [1.6, 0.05, 0.055]);
  const beam = new Mesh(moving.finish(), material);
  beam.name = 'weighing-beam';
  beam.position.y = TRADE_SCALE.pivot;
  const pans = [-1, 1].map((side) => {
    const pan = new Group();
    pan.name = side > 0 ? 'cargo-pan' : 'weights-pan';
    const builder = createMeshBuilder(256);
    boxSolid(builder, wood, frame, [-0.65, -0.04, -0.38], [0.65, TRADE_SCALE.panTop, 0.38]);
    for (const x of [-0.56, 0.56])
      for (const z of [-0.3, 0.3])
        beamSolid(builder, metal, frame, [x, 0, z], [0, TRADE_SCALE.chain, 0], 0.013);
    pan.add(new Mesh(builder.finish(), material));
    return pan;
  });
  const weightGeometry = createMeshBuilder(64);
  boxSolid(weightGeometry, metal, frame, [-0.1, 0, -0.09], [0.1, 0.16, 0.09]);
  const geometry = weightGeometry.finish();
  const weights = [-1, 0, 1].map((i) => {
    const weight = new Mesh(geometry, material);
    weight.name = `weighing-weight-${i + 1}`;
    return weight;
  });
  const group = new Group();
  group.name = 'dam-weighing-station';
  group.add(support, beam, ...pans, ...weights);
  function update(angle: number, share: number) {
    beam.rotation.z = angle;
    pans.forEach((pan, i) => {
      const side = i === 0 ? -1 : 1;
      pan.position.set(
        side * TRADE_SCALE.arm * Math.cos(angle),
        TRADE_SCALE.pivot + side * TRADE_SCALE.arm * Math.sin(angle) - TRADE_SCALE.chain,
        0
      );
    });
    weights.forEach((weight, i) => {
      const progress = Math.max(0, Math.min(1, share * 3 - i));
      const panY =
        TRADE_SCALE.pivot -
        TRADE_SCALE.arm * Math.sin(angle) -
        TRADE_SCALE.chain +
        TRADE_SCALE.panTop;
      weight.position.set(
        -TRADE_SCALE.arm * Math.cos(angle) + (i - 1) * 0.26,
        1.53 + (panY - 1.53) * progress + Math.sin(Math.PI * progress) * 0.25,
        0.85 * (1 - progress)
      );
    });
  }
  update(0, 0);
  return { group, update };
}
